/**
 * Leitura do fluxo para o Relatório Enterprise.
 * Frequência e permanência ficam separadas.
 * A rota principal não recebe o tempo integral de uma ramificação.
 */

import { convertLtToMinutes } from '../../utils/calculations';
import type { EnterpriseCardInput } from './enterpriseReportModel';

export type StageSignal =
  | 'TEMPO ELEVADO'
  | 'ESPERA ELEVADA'
  | 'EXCEÇÃO RARA'
  | 'ALTO IMPACTO'
  | 'RETRABALHO'
  | 'HANDOFF'
  | 'RETORNO'
  | 'NORMAL';

export interface ClassifiedStage {
  id: string;
  code: string;
  name: string;
  owner: string | null;
  route: 'principal' | 'condicional';
  frequency: number | null;
  frequencyLabel: string;
  permanenceMinutes: number | null;
  touchMinutes: number | null;
  waitMinutes: number | null;
  participationLabel: string;
  signals: StageSignal[];
  informedLabel: string;
}

export interface FlowException {
  name: string;
  occurrenceLabel: string;
  permanenceLabel: string;
  impactLabel: string;
  nature: string;
  message: string;
  highImpact: boolean;
}

export interface FlowReading {
  mainLeadMinutes: number | null;
  mainTouchMinutes: number | null;
  mainWaitMinutes: number | null;
  mainPcePercent: number | null;
  expectedLeadMinutes: number | null;
  expectedPcePercent: number | null;
  stages: ClassifiedStage[];
  exceptions: FlowException[];
  mainPermanences: Array<{ name: string; minutes: number }>;
  mainWaits: Array<{ name: string; minutes: number }>;
  activities: number;
  decisions: number;
  returns: number;
  handoffs: number | null;
  owners: string[];
  activityIds: string[];
  decisionIds: string[];
  returnEdgeKeys: string[];
  ownerById: Record<string, string | null>;
  highImpactCount: number;
  probabilityMissing: boolean;
  largestMainName: string | null;
}

interface FlowEdge {
  fromId: string;
  toId: string;
  handle: 'right' | 'bottom';
  probability: number | null;
  isReturn: boolean;
  isPrimary: boolean;
  siblingProbability: number | null;
}

function finiteOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

function readLeadMinutes(card: EnterpriseCardInput): number | null {
  const fromLt = finiteOrNull(card.lt);
  if (fromLt != null) {
    if (fromLt === 0) return 0;
    if (fromLt < 0) return null;
    return convertLtToMinutes(fromLt, card.tpUnit);
  }
  const fromTp = finiteOrNull(card.tp);
  if (fromTp == null || fromTp < 0) return null;
  if (fromTp === 0) return 0;
  return convertLtToMinutes(fromTp, card.tpUnit);
}

function readTouch(card: EnterpriseCardInput): number | null {
  const touch = finiteOrNull(card.tt);
  if (touch == null || touch < 0) return null;
  return touch;
}

function activeCards(cards: EnterpriseCardInput[]): EnterpriseCardInput[] {
  return cards.filter((card) => card.status !== 'removido' && card.status !== 'deletado' && card.tipo !== 'anotacao');
}

function isActivity(card: EnterpriseCardInput): boolean {
  return card.tipo === 'atividade' || (!card.tipo && Boolean(card.atividade));
}

function stageName(card: EnterpriseCardInput, index: number): string {
  const name = card.atividade?.trim() || card.cliente?.trim();
  return name || `Etapa ${String(index + 1).padStart(2, '0')}`;
}

function ownerOf(card: EnterpriseCardInput): string | null {
  const owner = card.cargo?.trim() || card.responsavel?.trim() || card.quem?.trim();
  return owner || null;
}

function presentToken(value: string): string {
  const clean = value.replace(/\s+/g, ' ').trim();
  const letters = clean.replace(/[^A-Za-zÀ-ÿ]/g, '');
  const upper = letters.toLocaleUpperCase('pt-BR');
  const lower = letters.toLocaleLowerCase('pt-BR');
  if (letters.length > 0 && letters.length <= 4 && letters === upper) return clean;
  const shout = letters.length > 4 && letters === upper;
  const plain = letters.length > 3 && letters === lower;
  if (!shout && !plain) return clean;
  return clean.toLocaleLowerCase('pt-BR').replace(/(^|[\s.])([a-zà-ÿ])/gi, (match) => match.toLocaleUpperCase('pt-BR'));
}

export function presentOwners(names: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const name of names) {
    const parts = name.split(/[,;]/).map((part) => part.trim()).filter(Boolean);
    for (const part of parts.length > 0 ? parts : [name]) {
      const clean = presentToken(part);
      const key = clean.toLocaleLowerCase('pt-BR');
      if (!clean || seen.has(key)) continue;
      seen.add(key);
      result.push(clean);
    }
  }
  return result;
}

export function formatExecutiveDuration(minutes: number): string {
  const safe = Math.max(0, minutes);
  if (safe < 60) return `${Math.round(safe)} min`;
  const workingDay = 480;
  if (safe < workingDay) {
    const hours = Math.floor(safe / 60);
    const mins = Math.round(safe - hours * 60);
    if (mins === 0) return hours === 1 ? '1 h' : `${hours} h`;
    return `${hours} h ${mins} min`;
  }
  const days = safe / workingDay;
  if (days < 30) {
    const rounded = Math.round(days * 10) / 10;
    if (rounded === 1) return '1 dia';
    const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',');
    return `${text} dias`;
  }
  const months = Math.round((days / 30) * 10) / 10;
  if (months === 1) return '1 mês';
  const text = Number.isInteger(months) ? String(months) : months.toFixed(1).replace('.', ',');
  return `${text} meses`;
}

function informedPermanenceLabel(_card: EnterpriseCardInput, minutes: number | null): string {
  if (minutes == null) return 'não informada';
  return formatExecutiveDuration(minutes);
}

function percentLabel(value: number): string {
  const pct = value * 100;
  const rounded = Math.round(pct * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',');
  return `${text}%`;
}

function isReturnEdge(source: EnterpriseCardInput, target: EnterpriseCardInput | undefined): boolean {
  if (!source || !target) return false;
  if (source.id === target.id) return true;
  const sourceX = source.posicao?.x;
  const targetX = target.posicao?.x;
  if (sourceX == null || targetX == null) return false;
  return sourceX > targetX + 10;
}

function handleOf(sourceHandle?: string): 'right' | 'bottom' {
  return sourceHandle === 'bottom' ? 'bottom' : 'right';
}

function buildEdges(cards: EnterpriseCardInput[]): FlowEdge[] {
  const byId = new Map(cards.map((card) => [card.id, card]));
  const edges: FlowEdge[] = [];
  for (const card of cards) {
    const outgoing = card.conexoes_saida ?? [];
    const decision = card.tipo === 'decisao';
    const sim = decision ? finiteOrNull(card.decisao_sim_pct) : null;
    const nao = decision ? finiteOrNull(card.decisao_nao_pct) : null;
    const forward = outgoing
      .map((connection) => {
        const target = byId.get(connection.targetId);
        return {
          connection,
          target,
          handle: handleOf(connection.sourceHandle),
          isReturn: isReturnEdge(card, target),
        };
      })
      .filter((item) => item.target);
    const forwardLive = forward.filter((item) => !item.isReturn);
    let primaryHandle: 'right' | 'bottom' | null = null;
    if (decision) {
      const hasRight = forwardLive.some((item) => item.handle === 'right');
      const hasBottom = forwardLive.some((item) => item.handle === 'bottom');
      if (sim != null && nao != null) {
        if (sim === nao) primaryHandle = hasRight ? 'right' : hasBottom ? 'bottom' : null;
        else primaryHandle = sim > nao ? (hasRight ? 'right' : null) : (hasBottom ? 'bottom' : null);
      } else if (sim != null && nao == null) primaryHandle = hasRight ? 'right' : null;
      else if (nao != null && sim == null) primaryHandle = hasBottom ? 'bottom' : null;
      else primaryHandle = hasRight ? 'right' : hasBottom ? 'bottom' : null;
    }
    const primaryProbability = !decision
      ? 1
      : primaryHandle === 'bottom'
        ? (nao == null ? null : nao / 100)
        : primaryHandle === 'right'
          ? (sim == null ? null : sim / 100)
          : null;
    const soleForward = !decision && forwardLive.length === 1 ? forwardLive[0] : null;
    for (const item of forward) {
      let probability: number | null = 1;
      let isPrimary = false;
      let siblingProbability: number | null = null;
      if (item.isReturn) {
        probability = null;
        isPrimary = false;
      } else if (decision) {
        const pct = item.handle === 'bottom' ? nao : sim;
        probability = pct == null ? null : pct / 100;
        isPrimary = item.handle === primaryHandle;
        siblingProbability = isPrimary ? null : primaryProbability;
      } else if (soleForward && item.connection.targetId === soleForward.connection.targetId && item.handle === soleForward.handle) {
        probability = 1;
        isPrimary = true;
      } else if (!decision && forwardLive.length > 1) {
        const continuation = [...forwardLive].sort((a, b) => (a.target?.posicao?.x ?? 0) - (b.target?.posicao?.x ?? 0))[0];
        isPrimary = continuation?.connection.targetId === item.connection.targetId && continuation.handle === item.handle;
        probability = isPrimary ? 1 : null;
        siblingProbability = isPrimary ? null : 1;
      }
      edges.push({
        fromId: card.id,
        toId: item.connection.targetId,
        handle: item.handle,
        probability,
        isReturn: item.isReturn,
        isPrimary,
        siblingProbability,
      });
    }
  }
  return edges;
}

function findStart(cards: EnterpriseCardInput[], edges: FlowEdge[]): EnterpriseCardInput | null {
  const marked = cards.find((card) => card.tipo === 'card_inicial');
  if (marked) return marked;
  const targets = new Set(edges.filter((edge) => !edge.isReturn).map((edge) => edge.toId));
  const roots = cards.filter((card) => !targets.has(card.id));
  if (roots.length === 0) return cards[0] ?? null;
  return [...roots].sort((a, b) => (a.posicao?.x ?? 0) - (b.posicao?.x ?? 0))[0] ?? null;
}

function walkMain(startId: string, edges: FlowEdge[]): string[] {
  const order: string[] = [];
  const seen = new Set<string>();
  let current: string | null = startId;
  while (current && !seen.has(current)) {
    seen.add(current);
    order.push(current);
    const next = edges.filter((edge) => edge.fromId === current && edge.isPrimary && !edge.isReturn);
    current = next[0]?.toId ?? null;
  }
  return order;
}

interface Reach {
  value: number;
  unknown: boolean;
}

function reachability(cards: EnterpriseCardInput[], edges: FlowEdge[], startId: string): Map<string, Reach> {
  const forward = edges.filter((edge) => !edge.isReturn);
  const acc = new Map<string, Reach>();
  const ensure = (id: string) => {
    const current = acc.get(id);
    if (current) return current;
    const created = { value: 0, unknown: false };
    acc.set(id, created);
    return created;
  };
  ensure(startId).value = 1;
  const indegree = new Map<string, number>();
  cards.forEach((card) => indegree.set(card.id, 0));
  forward.forEach((edge) => indegree.set(edge.toId, (indegree.get(edge.toId) ?? 0) + 1));
  const queue = cards.filter((card) => (indegree.get(card.id) ?? 0) === 0).map((card) => card.id);
  const seen = new Set<string>();
  while (queue.length > 0) {
    const id = queue.shift();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const source = ensure(id);
    for (const edge of forward.filter((item) => item.fromId === id)) {
      const target = ensure(edge.toId);
      if (source.unknown || edge.probability == null || (source.value === 0 && id !== startId)) target.unknown = true;
      else target.value += source.value * edge.probability;
      indegree.set(edge.toId, (indegree.get(edge.toId) ?? 1) - 1);
      if ((indegree.get(edge.toId) ?? 0) <= 0) queue.push(edge.toId);
    }
  }
  return acc;
}

function branchOrigin(edges: FlowEdge[], mainIds: Set<string>): Map<string, { probability: number | null; sibling: number | null }> {
  const meta = new Map<string, { probability: number | null; sibling: number | null }>();
  for (const edge of edges) {
    if (edge.isReturn || edge.isPrimary) continue;
    const queue = [edge.toId];
    const seen = new Set<string>();
    while (queue.length > 0) {
      const id = queue.shift();
      if (!id || seen.has(id) || mainIds.has(id)) continue;
      seen.add(id);
      if (!meta.has(id)) meta.set(id, { probability: edge.probability, sibling: edge.siblingProbability });
      for (const next of edges) {
        if (next.fromId === id && !next.isReturn) queue.push(next.toId);
      }
    }
  }
  return meta;
}

export function analyzeFlow(cards: EnterpriseCardInput[]): FlowReading {
  const visible = activeCards(cards);
  const byId = new Map(visible.map((card) => [card.id, card]));
  const edges = buildEdges(visible);
  const start = findStart(visible, edges);
  const mainOrder = start ? walkMain(start.id, edges) : [];
  const mainIds = new Set(mainOrder);
  const reach = start ? reachability(visible, edges, start.id) : new Map<string, Reach>();
  const origins = branchOrigin(edges, mainIds);
  const activities = visible.filter(isActivity);
  const decisions = visible.filter((card) => card.tipo === 'decisao');
  const returnEdges = edges.filter((edge) => edge.isReturn);
  const returnIds = new Set(returnEdges.flatMap((edge) => [edge.fromId, edge.toId]));

  const mainActivities = [
    ...mainOrder
      .map((id) => byId.get(id))
      .filter((card): card is EnterpriseCardInput => Boolean(card && isActivity(card))),
    ...activities.filter((card) => !mainIds.has(card.id) && !origins.has(card.id)),
  ];
  const conditionalActivities = activities.filter((card) => origins.has(card.id));
  const ordered = [...mainActivities, ...conditionalActivities];

  const draft = ordered.map((card, index) => {
    const permanenceMinutes = readLeadMinutes(card);
    const touchMinutes = readTouch(card);
    const waitMinutes = permanenceMinutes != null && touchMinutes != null
      ? Math.max(0, permanenceMinutes - touchMinutes)
      : null;
    const onConditional = origins.has(card.id);
    const onMain = !onConditional;
    const reached = reach.get(card.id);
    const walked = mainIds.has(card.id);
    const frequency = onConditional || walked
      ? (!reached || reached.unknown ? null : reached.value)
      : 1;
    const branch = onConditional ? origins.get(card.id) ?? null : null;
    return {
      id: card.id,
      index,
      card,
      name: stageName(card, index),
      owner: ownerOf(card),
      route: onMain ? 'principal' as const : 'condicional' as const,
      frequency,
      permanenceMinutes,
      touchMinutes,
      waitMinutes,
      branchProbability: branch?.probability ?? null,
      siblingProbability: branch?.sibling ?? null,
      informedLabel: informedPermanenceLabel(card, permanenceMinutes),
      rework: (() => {
        const accuracy = finiteOrNull(card.percentCompleteAccurate);
        return accuracy != null && accuracy < 100;
      })(),
      hasReturn: returnIds.has(card.id),
    };
  });

  const mainTimed = draft.filter((item) => item.route === 'principal' && item.permanenceMinutes != null);
  const mainLeadMinutes = mainTimed.length > 0
    ? mainTimed.reduce((sum, item) => sum + (item.permanenceMinutes ?? 0), 0)
    : null;
  const mainWithBoth = draft.filter((item) => item.route === 'principal' && item.permanenceMinutes != null && item.touchMinutes != null);
  const mainTouchIncomplete = draft.some((item) => item.route === 'principal' && item.permanenceMinutes != null && item.touchMinutes == null);
  const mainTouchMinutes = mainWithBoth.length > 0 && !mainTouchIncomplete
    ? mainWithBoth.reduce((sum, item) => sum + (item.touchMinutes ?? 0), 0)
    : mainWithBoth.length > 0 && mainTouchIncomplete
      ? null
      : null;
  const mainLeadForPce = mainTouchMinutes != null
    ? mainWithBoth.reduce((sum, item) => sum + (item.permanenceMinutes ?? 0), 0)
    : null;
  const mainPcePercent = mainLeadForPce != null && mainLeadForPce > 0 && mainTouchMinutes != null
    ? (mainTouchMinutes / mainLeadForPce) * 100
    : null;
  const waits = draft.filter((item) => item.route === 'principal' && item.waitMinutes != null);
  const mainWaitMinutes = waits.length > 0
    ? waits.reduce((sum, item) => sum + (item.waitMinutes ?? 0), 0)
    : null;

  const timed = draft.filter((item) => item.permanenceMinutes != null);
  const expectedLeadMinutes = timed.length > 0 && timed.every((item) => item.frequency != null)
    ? timed.reduce((sum, item) => sum + (item.frequency ?? 0) * (item.permanenceMinutes ?? 0), 0)
    : null;
  const expectedPcePercent = expectedLeadMinutes != null && expectedLeadMinutes > 0 && timed.every((item) => item.touchMinutes != null && item.frequency != null)
    ? (timed.reduce((sum, item) => sum + (item.frequency ?? 0) * (item.touchMinutes ?? 0), 0) / expectedLeadMinutes) * 100
    : null;

  const positiveMain = draft.filter((item) => item.route === 'principal' && (item.permanenceMinutes ?? 0) > 0);
  const maxPermanence = positiveMain.reduce((max, item) => Math.max(max, item.permanenceMinutes ?? 0), 0);
  const positiveWaits = draft.filter((item) => item.route === 'principal' && (item.waitMinutes ?? 0) > 0);
  const maxWait = positiveWaits.reduce((max, item) => Math.max(max, item.waitMinutes ?? 0), 0);

  const previousOwner = new Map<string, string | null>();
  let lastOwner: string | null = null;
  for (const item of draft.filter((entry) => entry.route === 'principal')) {
    previousOwner.set(item.id, lastOwner);
    if (item.owner) lastOwner = item.owner;
  }

  const stages: ClassifiedStage[] = draft.map((item) => {
    const signals: StageSignal[] = [];
    if (item.route === 'principal' && item.permanenceMinutes != null && maxPermanence > 0 && item.permanenceMinutes >= maxPermanence * 0.5) {
      signals.push('TEMPO ELEVADO');
    }
    if (item.route === 'principal' && item.waitMinutes != null && maxWait > 0 && item.waitMinutes >= maxWait * 0.5) {
      signals.push('ESPERA ELEVADA');
    }
    const rare = item.route === 'condicional'
      && item.branchProbability != null
      && item.siblingProbability != null
      && item.branchProbability * 2 <= item.siblingProbability;
    if (rare) signals.push('EXCEÇÃO RARA');
    const highImpact = rare && item.permanenceMinutes != null && maxPermanence > 0 && item.permanenceMinutes > maxPermanence;
    if (highImpact) signals.push('ALTO IMPACTO');
    if (item.rework) signals.push('RETRABALHO');
    const prior = previousOwner.get(item.id);
    if (item.route === 'principal' && item.owner && prior && prior !== item.owner) signals.push('HANDOFF');
    if (item.hasReturn) signals.push('RETORNO');
    if (signals.length === 0) signals.push('NORMAL');
    const frequencyLabel = item.frequency == null ? 'Não informada' : percentLabel(item.frequency);
    const participationLabel = item.route === 'condicional'
      ? 'Não entra na rota principal'
      : mainLeadMinutes != null && mainLeadMinutes > 0 && item.permanenceMinutes != null
        ? `${Math.round((item.permanenceMinutes / mainLeadMinutes) * 100)}% da rota principal`
        : '-';
    return {
      id: item.id,
      code: String(item.index + 1).padStart(2, '0'),
      name: item.name,
      owner: item.owner,
      route: item.route,
      frequency: item.frequency,
      frequencyLabel,
      permanenceMinutes: item.permanenceMinutes,
      touchMinutes: item.touchMinutes,
      waitMinutes: item.waitMinutes,
      participationLabel,
      signals,
      informedLabel: item.informedLabel,
    };
  });

  const exceptions: FlowException[] = stages
    .filter((stage) => stage.route === 'condicional')
    .map((stage) => {
      const highImpact = stage.signals.includes('ALTO IMPACTO');
      const rare = stage.signals.includes('EXCEÇÃO RARA');
      return {
        name: stage.name,
        occurrenceLabel: stage.frequency == null ? 'não informada' : stage.frequencyLabel,
        permanenceLabel: stage.permanenceMinutes == null ? 'não informada' : stage.informedLabel,
        impactLabel: highImpact ? 'ALTO' : rare ? 'condicional' : 'não classificado',
        nature: 'Exceção / rota condicional',
        message: stage.frequency == null
          ? 'A probabilidade da saída desta rota não foi informada. O tempo não foi somado à rota principal.'
          : highImpact
            ? 'A probabilidade de passagem é baixa e a permanência é elevada nos pedidos que seguem essa rota.'
            : 'A permanência vale somente para os pedidos que passam por esta rota.',
        highImpact,
      };
    });

  const mainPermanences = stages
    .filter((stage) => stage.route === 'principal' && (stage.permanenceMinutes ?? 0) > 0)
    .map((stage) => ({ name: stage.name, minutes: stage.permanenceMinutes ?? 0 }))
    .sort((a, b) => b.minutes - a.minutes);
  const mainWaitsRank = stages
    .filter((stage) => stage.route === 'principal' && (stage.waitMinutes ?? 0) > 0)
    .map((stage) => ({ name: stage.name, minutes: stage.waitMinutes ?? 0 }))
    .sort((a, b) => b.minutes - a.minutes);

  const owners = presentOwners(activities.map(ownerOf).filter((owner): owner is string => Boolean(owner)));
  let handoffs: number | null = null;
  const mainOwners = mainActivities.map(ownerOf).filter((owner): owner is string => Boolean(owner));
  if (mainOwners.length > 0) {
    handoffs = mainOwners.slice(1).filter((owner, index) => owner !== mainOwners[index]).length;
  }

  return {
    mainLeadMinutes,
    mainTouchMinutes,
    mainWaitMinutes,
    mainPcePercent,
    expectedLeadMinutes,
    expectedPcePercent,
    stages,
    exceptions,
    mainPermanences,
    mainWaits: mainWaitsRank,
    activities: activities.length,
    decisions: decisions.length,
    returns: returnEdges.length,
    handoffs,
    owners,
    activityIds: activities.map((card) => card.id),
    decisionIds: decisions.map((card) => card.id),
    returnEdgeKeys: returnEdges.map((edge) => `${edge.fromId}>${edge.toId}`),
    ownerById: Object.fromEntries(activities.map((card) => [card.id, ownerOf(card)])),
    highImpactCount: exceptions.filter((item) => item.highImpact).length,
    probabilityMissing: stages.some((stage) => stage.route === 'condicional' && stage.frequency == null),
    largestMainName: mainPermanences[0]?.name ?? null,
  };
}

export function scenarioIsComparable(current: EnterpriseCardInput[], other: EnterpriseCardInput[] | null | undefined): boolean {
  if (!other || other.length === 0) return false;
  const currentIds = new Set(activeCards(current).filter(isActivity).map((card) => card.id));
  const otherActivities = activeCards(other).filter(isActivity);
  if (otherActivities.length === 0 && activeCards(other).every((card) => card.tipo !== 'decisao')) return false;
  const otherIds = new Set(otherActivities.map((card) => card.id));
  const shared = [...currentIds].filter((id) => otherIds.has(id));
  if (shared.length === 0) return otherActivities.length > 0;
  const currentById = new Map(current.map((card) => [card.id, card]));
  const otherById = new Map(other.map((card) => [card.id, card]));
  const signature = (card: EnterpriseCardInput) => [
    card.lt ?? '',
    card.tt ?? '',
    card.status ?? '',
    card.cargo ?? '',
    card.atividade ?? '',
    (card.conexoes_saida ?? []).map((item) => `${item.targetId}:${item.sourceHandle ?? ''}`).sort().join(','),
  ].join('|');
  const diverged = shared.some((id) => signature(currentById.get(id)!) !== signature(otherById.get(id)!));
  const extra = [...otherIds].some((id) => !currentIds.has(id));
  const missing = [...currentIds].some((id) => !otherIds.has(id));
  if (missing && !diverged && !extra) return false;
  return true;
}

export interface EvolutionRow {
  indicator: string;
  current: string;
  kaizen: string;
  future: string;
  variation: string;
  kaizenNote: string | null;
}

function formatCount(value: number | null): string | null {
  if (value == null) return null;
  return String(value);
}

function decimalPt(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',');
}

export function projectedVariation(current: number, target: number, kind: 'duration' | 'points' | 'count'): string {
  if (target === current) return 'Sem alteração calculada';
  if (kind === 'points') {
    const delta = target - current;
    const text = `${delta > 0 ? '+' : '-'}${decimalPt(Math.abs(delta))} p.p.`;
    return delta > 0 ? `${text} · melhoria proposta` : `${text} · redução projetada`;
  }
  if (kind === 'count') {
    const delta = target - current;
    const pct = current !== 0 ? Math.abs((current - target) / current) * 100 : null;
    const signed = `${delta > 0 ? '+' : '-'}${Math.abs(delta)}`;
    const pctText = pct == null ? '' : ` · redução de ${decimalPt(pct)}%`;
    if (delta < 0) return `${signed}${pctText}`;
    return pct == null ? `${signed} · variação projetada` : `${signed} · aumento de ${decimalPt(pct)}%`;
  }
  if (current === 0) return 'Variação projetada';
  const pct = ((current - target) / current) * 100;
  if (pct > 0) return `-${decimalPt(pct)}% · redução projetada`;
  return `+${decimalPt(Math.abs(pct))}% · variação projetada`;
}

export function evolutionRows(
  current: FlowReading,
  kaizen: FlowReading | null,
  future: FlowReading | null,
  formatMinutes: (minutes: number | null) => string | null,
): EvolutionRow[] {
  const states = [current, kaizen, future].filter((item): item is FlowReading => item != null);
  if (states.length < 2) return [];
  const candidates: Array<{ indicator: string; pick: (flow: FlowReading) => number | null; format: (value: number) => string; kind: 'duration' | 'points' | 'count' }> = [
    { indicator: 'Lead time da rota principal', pick: (flow) => flow.mainLeadMinutes, format: (value) => formatMinutes(value) ?? '-', kind: 'duration' },
    { indicator: 'Lead time esperado', pick: (flow) => flow.expectedLeadMinutes, format: (value) => formatMinutes(value) ?? '-', kind: 'duration' },
    { indicator: 'Realização', pick: (flow) => flow.mainTouchMinutes, format: (value) => formatMinutes(value) ?? '-', kind: 'duration' },
    { indicator: 'Espera', pick: (flow) => flow.mainWaitMinutes, format: (value) => formatMinutes(value) ?? '-', kind: 'duration' },
    { indicator: 'PCE da rota principal', pick: (flow) => flow.mainPcePercent, format: (value) => `${value.toFixed(1).replace('.', ',')}%`, kind: 'points' },
    { indicator: 'Etapas', pick: (flow) => flow.activities, format: (value) => formatCount(value) ?? '-', kind: 'count' },
    { indicator: 'Decisões', pick: (flow) => flow.decisions, format: (value) => formatCount(value) ?? '-', kind: 'count' },
    { indicator: 'Handoffs', pick: (flow) => flow.handoffs, format: (value) => formatCount(value) ?? '-', kind: 'count' },
    { indicator: 'Retornos', pick: (flow) => flow.returns, format: (value) => formatCount(value) ?? '-', kind: 'count' },
    { indicator: 'Exceções de alto impacto', pick: (flow) => flow.highImpactCount, format: (value) => formatCount(value) ?? '-', kind: 'count' },
  ];
  const rows: EvolutionRow[] = [];
  for (const candidate of candidates) {
    const values = states.map((flow) => candidate.pick(flow));
    if (values.some((value) => value == null)) continue;
    const currentValue = candidate.pick(current);
    const kaizenValue = kaizen ? candidate.pick(kaizen) : null;
    const futureValue = future ? candidate.pick(future) : null;
    const target = futureValue ?? kaizenValue;
    const variation = currentValue == null || target == null
      ? 'Sem base para comparar'
      : projectedVariation(currentValue, target, candidate.kind);
    rows.push({
      indicator: candidate.indicator,
      current: currentValue == null ? '-' : candidate.format(currentValue),
      kaizen: kaizen ? (kaizenValue == null ? '-' : candidate.format(kaizenValue)) : 'não gerado',
      future: future ? (futureValue == null ? '-' : candidate.format(futureValue)) : 'não gerado',
      variation,
      kaizenNote: kaizenValue != null && currentValue != null && kaizenValue === currentValue ? 'Sem alteração calculada' : null,
    });
  }
  return rows;
}

export function structuralChanges(currentCards: EnterpriseCardInput[], otherCards: EnterpriseCardInput[], label: string): string[] {
  const currentFlow = analyzeFlow(currentCards);
  const otherFlow = analyzeFlow(otherCards);
  const notes: string[] = [];
  const currentActivities = new Set(currentFlow.activityIds);
  const otherActivities = new Set(otherFlow.activityIds);
  const removed = [...currentActivities].filter((id) => !otherActivities.has(id));
  const added = [...otherActivities].filter((id) => !currentActivities.has(id));
  if (removed.length > 0) notes.push(`${removed.length} etapa${removed.length === 1 ? '' : 's'} removida${removed.length === 1 ? '' : 's'} no ${label}.`);
  if (added.length > 0) notes.push(`${added.length} etapa${added.length === 1 ? '' : 's'} adicionada${added.length === 1 ? '' : 's'} no ${label}.`);
  const removedDecisions = currentFlow.decisionIds.filter((id) => !otherFlow.decisionIds.includes(id));
  if (removedDecisions.length > 0) notes.push(`${removedDecisions.length} decisão${removedDecisions.length === 1 ? '' : 'ões'} eliminada${removedDecisions.length === 1 ? '' : 's'} no ${label}.`);
  const removedReturns = currentFlow.returnEdgeKeys.filter((key) => !otherFlow.returnEdgeKeys.includes(key));
  if (removedReturns.length > 0) notes.push(`${removedReturns.length} retorno${removedReturns.length === 1 ? '' : 's'} eliminado${removedReturns.length === 1 ? '' : 's'} no ${label}.`);
  if (currentFlow.handoffs != null && otherFlow.handoffs != null && otherFlow.handoffs < currentFlow.handoffs) {
    notes.push(`Handoffs reduzidos no ${label}: ${currentFlow.handoffs} para ${otherFlow.handoffs}.`);
  }
  if (currentFlow.mainWaitMinutes != null && otherFlow.mainWaitMinutes != null && otherFlow.mainWaitMinutes < currentFlow.mainWaitMinutes) {
    notes.push(`Espera da rota principal reduzida no ${label}.`);
  }
  const shared = [...currentActivities].filter((id) => otherActivities.has(id));
  const ownerChanges = shared.filter((id) => (currentFlow.ownerById[id] ?? null) !== (otherFlow.ownerById[id] ?? null) && currentFlow.ownerById[id] && otherFlow.ownerById[id]);
  if (ownerChanges.length > 0) notes.push(`Responsabilidade alterada em ${ownerChanges.length} etapa${ownerChanges.length === 1 ? '' : 's'} no ${label}.`);
  return notes;
}

export interface FlowPathStep {
  id: string;
  kind: 'inicio' | 'fim' | 'decisao' | 'atividade' | 'etapa';
  name: string;
}

export interface FlowPath {
  code: string;
  key: string;
  role: 'principal' | 'condicional';
  title: string;
  sequence: string;
  steps: FlowPathStep[];
  activityIds: string[];
  probability: number | null;
  probabilityLabel: string;
  tpMinutes: number | null;
  traMinutes: number | null;
  waitMinutes: number | null;
  pcePercent: number | null;
  loopNote: string | null;
}

const MAX_FLOW_PATHS = 40;

function stepOf(card: EnterpriseCardInput, index: number): FlowPathStep {
  if (card.tipo === 'card_inicial') return { id: card.id, kind: 'inicio', name: 'Início' };
  if (card.tipo === 'card_final') return { id: card.id, kind: 'fim', name: 'Fim' };
  if (card.tipo === 'decisao') return { id: card.id, kind: 'decisao', name: card.atividade?.trim() || 'Decisão' };
  if (isActivity(card)) return { id: card.id, kind: 'atividade', name: stageName(card, index) };
  return { id: card.id, kind: 'etapa', name: stageName(card, index) };
}

function sumComplete(values: Array<number | null>): number | null {
  if (values.length === 0 || values.some((value) => value == null)) return null;
  return values.reduce((sum, value) => sum + (value ?? 0), 0);
}

/** Caminhos de ida do grafo. Reusa arestas, retorno e probabilidade do Enterprise. */
export function enumerateFlowPaths(cards: EnterpriseCardInput[]): { paths: FlowPath[]; truncated: boolean } {
  const visible = activeCards(cards);
  const byId = new Map(visible.map((card) => [card.id, card]));
  const edges = buildEdges(visible);
  const start = findStart(visible, edges);
  if (!start) return { paths: [], truncated: false };
  const forward = edges.filter((edge) => !edge.isReturn);
  const mainIds = new Set(
    walkMain(start.id, edges)
      .map((id) => byId.get(id))
      .filter((card): card is EnterpriseCardInput => Boolean(card && isActivity(card)))
      .map((card) => card.id),
  );
  const drafts: Array<Omit<FlowPath, 'code' | 'title'> & { primary: boolean }> = [];
  let truncated = false;

  const visit = (id: string, stack: string[], probability: number | null, primary: boolean, sawLoop: boolean) => {
    if (drafts.length >= MAX_FLOW_PATHS) {
      truncated = true;
      return;
    }
    if (stack.includes(id)) return;
    const nextStack = [...stack, id];
    const outgoing = forward.filter((edge) => edge.fromId === id);
    const live = outgoing.filter((edge) => !nextStack.includes(edge.toId));
    const closesLoop = sawLoop || outgoing.some((edge) => nextStack.includes(edge.toId)) || edges.some((edge) => edge.isReturn && edge.fromId === id);
    if (live.length === 0) {
      const steps = nextStack
        .map((stepId) => byId.get(stepId))
        .filter((card): card is EnterpriseCardInput => Boolean(card))
        .map((card, index) => stepOf(card, index));
      const activities = steps
        .filter((step) => step.kind === 'atividade')
        .map((step) => byId.get(step.id))
        .filter((card): card is EnterpriseCardInput => Boolean(card));
      const tpMinutes = sumComplete(activities.map((card) => readLeadMinutes(card)));
      const traMinutes = sumComplete(activities.map((card) => readTouch(card)));
      const waitMinutes = tpMinutes != null && traMinutes != null ? Math.max(0, tpMinutes - traMinutes) : null;
      const activityIds = activities.map((card) => card.id);
      drafts.push({
        key: activityIds.join('>'),
        role: 'condicional',
        sequence: steps.map((step) => step.name).join(' > '),
        steps,
        activityIds,
        probability,
        probabilityLabel: probability == null ? 'Não informada' : percentLabel(probability),
        tpMinutes,
        traMinutes,
        waitMinutes,
        pcePercent: tpMinutes != null && traMinutes != null && tpMinutes > 0 ? (traMinutes / tpMinutes) * 100 : null,
        loopNote: closesLoop ? 'Retorno identificado. A atividade não é somada novamente.' : null,
        primary,
      });
      return;
    }
    for (const edge of live) {
      const nextProbability = probability == null || edge.probability == null ? null : probability * edge.probability;
      visit(edge.toId, nextStack, nextProbability, primary && edge.isPrimary, closesLoop);
    }
  };

  visit(start.id, [], 1, true, false);
  const ordered = [
    ...drafts.filter((path) => path.primary),
    ...drafts.filter((path) => !path.primary).sort((a, b) => (b.probability ?? -1) - (a.probability ?? -1)),
  ];
  const paths = ordered.map((path, index) => {
    const branch = path.steps.find((step) => step.kind === 'atividade' && !mainIds.has(step.id))?.name;
    const { primary: _primary, ...rest } = path;
    return {
      ...rest,
      role: path.primary ? 'principal' as const : 'condicional' as const,
      code: `CAMINHO ${String(index + 1).padStart(2, '0')}`,
      title: path.primary ? 'Rota principal' : branch ? `Rota condicional · ${branch}` : 'Rota condicional',
    };
  });
  return { paths, truncated };
}
